import { IController, IControllerService, ILocationProvider, auto, extend, module } from "angular";
import { ParamTypeDefinition, UrlMatcherFactory } from "@uirouter/angular";
import uiRouter, { Ng1ViewDeclaration, StateProvider } from "@uirouter/angularjs";
import { upgradeModule } from "@uirouter/angular-hybrid";
import lazyLoad from "oclazyload/dist/modules/ocLazyLoad.core.js";
import "oclazyload/dist/modules/ocLazyLoad.loaders.core.js";
import { ROUTE_TRACING } from "@gtmhub/build";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { shouldUseQuantiveUrlBehavior } from "@gtmhub/env";
import env from "@gtmhub/env/env.module";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import uiBootstrap from "@gtmhub/ui-bootstrap.module";
import { UiDrawerService } from "@webapp/ui/drawer/services/drawer.service";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import upgrade from "../upgrade/module";
import { configureCdn } from "./cdn";
import { decorateModal } from "./decorators/modal";
import { IStateInit } from "./models";
import { enableRouteTracing } from "./tracing";
import { stateParamsToComponentBindings } from "./views-decorators/params-to-bindings";

const mod = module("routing", [uiRouter, upgradeModule.name, upgrade, uiBootstrap, lazyLoad, env]);

mod.downgradeInjectable("UiModalService", UiModalService);
mod.downgradeInjectable("UiDrawerService", UiDrawerService);
mod.service("Ng1UiModalService", Ng1UiModalService);

mod.config([
  "$locationProvider",
  "$urlMatcherFactoryProvider",
  ($locationProvider: ILocationProvider, $urlMatcherFactory: UrlMatcherFactory) => {
    $urlMatcherFactory.type("boolean", {
      decode: function (val) {
        return val == "true";
      },
      encode: function (val) {
        return val ? "true" : "false";
      },
      equals: function (this: ParamTypeDefinition, a, b) {
        return this.is(a) && a == b;
      },
      is: function (val) {
        return [true, false].indexOf(val) !== -1;
      },
      pattern: /false|true/,
    });

    if (shouldUseQuantiveUrlBehavior()) {
      $locationProvider.html5Mode({ enabled: true, requireBase: false, rewriteLinks: true });
    } else {
      $locationProvider.html5Mode(false);
    }
    $locationProvider.hashPrefix("");
  },
]);

const viewsDecorators = [stateParamsToComponentBindings];
mod.config([
  "$stateProvider",
  ($stateProvider: StateProvider) => {
    $stateProvider.decorator("views", function (state, parent) {
      const views: { [name: string]: Ng1ViewDeclaration } = parent(state);
      for (const view of Object.values(views)) {
        for (const handle of viewsDecorators) {
          handle(state, view);
        }
      }
      return views;
    });
  },
]);

mod.config([
  "$provide",
  ($provide: auto.IProvideService) => {
    $provide.decorator("$controller", [
      "$delegate",
      "$rootScope",
      // eslint-disable-next-line @typescript-eslint/ban-types
      ($delegate: Function, $rootScope: ITraceRootScopeService) => {
        const tryRunStateInit = (ctrl: unknown) => {
          const stateInitCtrl = ctrl as IStateInit;
          if (typeof stateInitCtrl.stateInit === "function") {
            $rootScope.traceInit(stateInitCtrl.stateInit.bind(stateInitCtrl));
          }
        };

        const tryPatchOnInit = (ctrl: unknown) => {
          const initCtrl = ctrl as IController;
          if (initCtrl.$onInit) {
            const origOnInit = initCtrl.$onInit;
            initCtrl.$onInit = function () {
              $rootScope.traceInit(() => origOnInit.bind(initCtrl)());
            };
          }
        };
        return function stateInitAwareController(this: IControllerService, ...args: unknown[]) {
          const ctrl = $delegate.apply(this, args);
          if (typeof ctrl === "function") {
            // Angular.js may create a lazy wrapper for initialization, so
            // we will replace it in a way we can call `stateInit`.
            // PS: search for `$controllerInit` in the Angular.js source code
            // if you want to see their implementation.
            if ("instance" in ctrl && "identifier" in ctrl) {
              return extend(
                function stateAwareControllerInit() {
                  const instance = ctrl();
                  tryRunStateInit(instance);
                  tryPatchOnInit(instance);
                  return instance;
                },
                {
                  instance: ctrl.instance,
                  identifier: ctrl.identifier,
                }
              );
            }
          } else {
            tryRunStateInit(ctrl);
          }
          return ctrl;
        };
      },
    ]);
  },
]);

if (ROUTE_TRACING) {
  enableRouteTracing(mod);
}

configureCdn(mod);

decorateModal(mod);

export default mod.name;
